import React from 'react';
import { CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const SupplyChainManagementPage = () => {
  return (
    <div className="min-h-screen pt-20">
      <section className="relative h-80 flex items-center justify-center overflow-hidden bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900">
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Supply Chain Management</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Logistics & Procurement for Oil & Gas Projects
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Supply Chain Management for <span className="text-red-600">Oil & Gas Projects</span>
            </h2>
            <div className="w-24 h-1 bg-red-600 mb-8" />
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Efficient supply chain management is essential to the success of oil and gas projects, where long lead equipment, global vendor networks, and strict regulatory requirements demand disciplined planning and execution. A well structured supply chain ensures timely delivery of materials, optimized costs, and seamless coordination between engineering, procurement, logistics, and construction teams.
            </p>
          </div>

          {/* Strategic Procurement */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Strategic Procurement and Sourcing</h3>
            <p className="text-slate-700 mb-6">
              Procurement in oil and gas requires deep technical understanding and strong commercial acumen.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                'Development of procurement strategies aligned with project schedules and budgets',
                'Global sourcing of equipment, materials, and services',
                'Preparation of RFQs, bid evaluations, and commercial negotiations',
                'Contracting strategies for long lead and critical equipment'
              ].map((item, idx) => (
                <Card key={idx} className="border-none shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-slate-700 mt-6 italic">
              Total Vision Oil and Gas Fields Services and Consultancy LLC SPC supports clients by ensuring procurement decisions are technically sound, commercially competitive, and aligned with project objectives.
            </p>
          </div>

          {/* Vendor Management */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Vendor Management and Technical Coordination</h3>
            <p className="text-slate-700 mb-6">
              Vendor performance directly impacts project timelines and quality.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                'Vendor capability assessments and audits',
                'Review of vendor documentation, data sheets, and manufacturing plans',
                'Coordination of design clarifications between engineering and OEMs',
                'Monitoring of fabrication, testing, and inspection activities',
                'Expediting to ensure timely delivery of materials and equipment'
              ].map((item, idx) => (
                <Card key={idx} className="border-none shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-slate-700 mt-6 italic">
              Total Vision plays a key role in maintaining strong vendor relationships and ensuring all procured items meet technical and quality requirements.
            </p>
          </div>

          {/* Logistics Planning */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Logistics Planning and Material Handling</h3>
            <p className="text-slate-700 mb-6">
              Oil and gas logistics involve complex global movements, regulatory compliance, and strict safety requirements.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                'International freight management (air, sea, and land)',
                'Customs clearance, import/export documentation, and regulatory compliance',
                'Heavy lift and oversized cargo planning',
                'Warehousing, storage, and inventory control',
                'Material preservation, packing, and handling procedures'
              ].map((item, idx) => (
                <Card key={idx} className="border-none shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-slate-700 mt-6 italic">
              Total Vision ensures that logistics operations are executed safely, efficiently, and in full compliance with regional and international regulations.
            </p>
          </div>

          {/* Supply Chain Risk Management */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Supply Chain Risk Management</h3>
            <p className="text-slate-700 mb-6">
              Proactive risk management protects project schedules and budgets.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                'Identification of supply chain risks such as delays, shortages, and geopolitical constraints',
                'Development of mitigation strategies and contingency plans',
                'Monitoring of market trends, pricing fluctuations, and vendor capacity',
                'Early engagement with long lead suppliers to reduce schedule uncertainty'
              ].map((item, idx) => (
                <Card key={idx} className="border-none shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-slate-700 mt-6 italic">
              Total Vision integrates risk management into every stage of the supply chain to ensure predictable and resilient project execution.
            </p>
          </div>

          {/* Material Tracking */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Material Tracking and Documentation Control</h3>
            <p className="text-slate-700 mb-6">
              Accurate tracking and documentation are essential for quality assurance and regulatory compliance.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                'Material tracking from purchase order to site delivery',
                'Use of digital systems for traceability and documentation management',
                'Verification of material certificates, inspection reports, and compliance records',
                'Coordination with construction teams for material issuance and work front planning'
              ].map((item, idx) => (
                <Card key={idx} className="border-none shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-slate-700 mt-6 italic">
              Total Vision supports clients with robust documentation and tracking systems that enhance transparency and control.
            </p>
          </div>

          {/* Integration */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Integration with Engineering and Construction</h3>
            <p className="text-slate-700 mb-6">
              A successful supply chain is fully aligned with engineering deliverables and construction priorities.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                'Coordination with engineering for technical requisitions and vendor data reviews',
                'Alignment of procurement schedules with construction sequencing',
                'Support for construction teams through timely material availability',
                'Interface management across engineering, procurement, logistics, and site teams'
              ].map((item, idx) => (
                <Card key={idx} className="border-none shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-slate-700 mt-6 italic">
              Total Vision ensures seamless integration across project functions, reducing delays and improving overall project performance.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-3xl font-bold text-slate-900 mb-6">Why Supply Chain Management Matters in Oil & Gas</h3>
            <p className="text-lg text-slate-700 leading-relaxed mb-8">
              A well managed supply chain ensures:
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {[
                'On time delivery of critical equipment and materials',
                'Reduced project risks and cost overruns',
                'Strong vendor performance and quality compliance',
                'Efficient logistics and safe material handling',
                'Improved coordination across the project lifecycle'
              ].map((item, idx) => (
                <Card key={idx} className="border-none shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base font-medium">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-lg text-slate-700 leading-relaxed italic">
              Total Vision Oil and Gas Fields Services and Consultancy LLC SPC brings technical expertise, global vendor insight, and disciplined execution to every stage of the supply chain, ensuring reliable and cost effective project delivery.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SupplyChainManagementPage;